import type { ReactElement } from 'react';
import styled from 'styled-components';
import { Tag, Tooltip } from '../../components';
import { store } from '../../store';
import type { ScheduledEvent } from '../event-details';
import type { PreferenceKey } from './shot-rules';
import { SHOT_RULES, SHOT_TOGGLES, TONE_STYLE } from './shot-rules';

interface Props {
  scheduledEvent: ScheduledEvent;
}

interface ShotTag {
  key: string;
  className: string;
  icon: string;
  text: string;
  tooltip: string;
}

type NoteKey = 'audio_notes' | 'hardware_notes' | 'image_notes' | 'network_notes' | 'technical_notes';

const NOTE_TYPES: { key: NoteKey; label: string }[] = [
  { key: 'audio_notes', label: 'Audio' },
  { key: 'hardware_notes', label: 'Hardware' },
  { key: 'image_notes', label: 'Image' },
  { key: 'network_notes', label: 'Network' },
  { key: 'technical_notes', label: 'Technical' }
];

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  margin-bottom: 13px;

  .title {
    font-weight: 700;
    font-size: 15px;
    color: rgb(240, 242, 245);
  }
`;

const IconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(176, 186, 209, 0.7);
  text-decoration: none;
  flex-shrink: 0;
  transition: all var(--transition);
  font-size: 13px;

  &:hover,
  &:focus {
    outline: none;
    color: rgb(159, 176, 255);
    border-color: rgba(255, 255, 255, 0.22);
  }
`;

const Box = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  padding: 15px 16px;
  background: rgba(17, 27, 51, 0.3);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Entry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;

  .title {
    font-weight: 700;
    font-size: 10px;
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: rgba(159, 176, 255, 0.85);
  }

  .text {
    font-size: 13px;
    line-height: 1.55;
    color: rgb(215, 220, 237);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .empty {
    font-size: 13px;
    font-weight: 400;
    font-style: italic;
    color: rgba(176, 186, 209, 0.4);
  }
`;

const ShotRulesBox = styled.div`
  margin-top: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  padding: 13px 14px;
  background: rgba(17, 27, 51, 0.3);

  .rules-title {
    margin-bottom: 9px;
    font-weight: 700;
    font-size: 14px;
    color: var(--white);
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }
`;

/*
 * One titled block of notes. Uncapped on purpose, like every other block here: these are read
 * before going on air, and a scroller inside them hides the half nobody thought to scroll to.
 */
const NoteSection = styled.div`
  margin-top: 16px;
`;

/* Same chrome as IconLink, widened to carry a label: the client asked for a named button. */
const CueSheetLink = styled(IconLink)`
  width: auto;
  gap: 7px;
  padding: 0 11px;
  font-size: 12px;
  font-weight: 600;
`;

/* The videographer block starts a new subject, so it sits further from what precedes it. */
const SubHeader = styled(Header)`
  margin-top: 26px;
`;

const NoteBox = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  padding: 15px 16px;
  background: rgba(17, 27, 51, 0.3);

  .note-title {
    margin-bottom: 9px;
    font-weight: 700;
    font-size: 14px;
    color: var(--white);
  }

  .note-text {
    font-size: 13px;
    line-height: 1.55;
    color: rgb(215, 220, 237);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .note-empty {
    font-size: 13px;
    font-weight: 400;
    font-style: italic;
    color: rgba(176, 186, 209, 0.4);
  }
`;

type PreferencesMap = Partial<Record<PreferenceKey, { option?: number | null } | null>>;

/*
 * ScheduledEvent doesn't type `preferences` (the legacy view casts it too), and
 * slidesEnabled / montageEnabled aren't fetched — read them off the event when
 * present, absent otherwise.
 */
interface PreferencesSource {
  preferences?: PreferencesMap | null;
  slidesEnabled?: boolean;
  montageEnabled?: boolean;
}

const shotTags = (scheduledEvent: ScheduledEvent): ShotTag[] => {
  const source = scheduledEvent as PreferencesSource & ScheduledEvent;

  const ruleTags = SHOT_RULES.flatMap((rule): ShotTag[] => {
    const option = source.preferences?.[rule.key]?.option;
    const optionRule = typeof option === 'number' ? rule.options[option] : undefined;

    if (!optionRule) {
      return [];
    }

    const style = TONE_STYLE[optionRule.tone];

    return [{ key: rule.key, className: style.className, icon: style.icon, text: optionRule.text, tooltip: optionRule.tooltip }];
  });

  const toggleTags = SHOT_TOGGLES.flatMap((toggle): ShotTag[] => {
    if (!source[toggle.key]) {
      return [];
    }

    return [{ key: toggle.key, className: TONE_STYLE.green.className, icon: TONE_STYLE.green.icon, text: toggle.text, tooltip: toggle.tooltip }];
  });

  return [...ruleTags, ...toggleTags];
};

export const NotesTab = ({ scheduledEvent }: Props): ReactElement => {
  const adminDashboardUri = store((v) => v.environment.ADMIN_DASHBOARD_URI);
  const clientId = scheduledEvent.location?.user?.id;
  const userNotes = scheduledEvent.location?.user?.userNotes;
  const supportNotes = (scheduledEvent.location?.user?.support_notes ?? '').trim();
  const tags = shotTags(scheduledEvent);

  /*
   * The two halves of what a videographer is briefed with: what is true of this client every
   * time, held in the admin DB, and what is true of this event only.
   */
  const permanentNotes = (scheduledEvent.location?.user?.event_operator_notes ?? '').trim();
  const eventNotes = (scheduledEvent.operator_notes ?? '').trim();
  const cueSheetUrl = scheduledEvent.cue_sheet_url ?? '';

  return (
    <div>
      <Header>
        <span className='title'>Production Notes</span>

        {clientId !== undefined && (
          <IconLink
            href={`${adminDashboardUri}/clients/${clientId}/profile`}
            target='_blank'
            rel='noreferrer'
            title='Edit in Admin Dashboard'
          >
            <i className='far fa-external-link' />
          </IconLink>
        )}
        {/* Right-aligned in-app edit button goes here later. */}
      </Header>

      <Box>
        {NOTE_TYPES.map(({ key, label }) => {
          const value = (userNotes?.[key] ?? '').trim();

          return (
            <Entry key={key}>
              <div className='title'>{label}</div>
              {value ? (
                <div className='text'>{value}</div>
              ) : (
                <div className='empty'>No notes yet.</div>
              )}
            </Entry>
          );
        })}
      </Box>

      {tags.length > 0 && (
        <ShotRulesBox>
          <div className='rules-title'>Shot Rules</div>
          <div className='tags'>
            {tags.map((tag) => (
              <Tooltip key={tag.key} placement='bottom-left' maxWidth={260} content={tag.tooltip}>
                <Tag className={tag.className} icon={tag.icon}>
                  {tag.text}
                </Tag>
              </Tooltip>
            ))}
          </div>
        </ShotRulesBox>
      )}

      <NoteSection>
        <NoteBox>
          <div className='note-title'>Support Notes</div>
          {supportNotes ? (
            <div className='note-text'>{supportNotes}</div>
          ) : (
            <div className='note-empty'>No support notes.</div>
          )}
        </NoteBox>
      </NoteSection>

      <SubHeader>
        <span className='title'>Videographer</span>

        {/* Hidden rather than disabled when absent: plenty of events never get a cue sheet. */}
        {cueSheetUrl ? (
          <CueSheetLink
            href={cueSheetUrl}
            target='_blank'
            rel='noreferrer'
            title='Open the uploaded cue sheet'
          >
            <i className='far fa-external-link' />
            Cue Sheet
          </CueSheetLink>
        ) : null}
      </SubHeader>

      <NoteBox>
        <div className='note-title'>Event Notes</div>
        {eventNotes ? (
          <div className='note-text'>{eventNotes}</div>
        ) : (
          <div className='note-empty'>No event notes.</div>
        )}
      </NoteBox>

      <NoteSection>
        <NoteBox>
          <div className='note-title'>Permanent Notes</div>
          {permanentNotes ? (
            <div className='note-text'>{permanentNotes}</div>
          ) : (
            <div className='note-empty'>No permanent notes.</div>
          )}
        </NoteBox>
      </NoteSection>
    </div>
  );
};
